import { useEffect, useState } from "react";
import styled from "styled-components";
import { styled as muiStyled } from "@mui/material/styles";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";

import { Grid } from "@mui/material";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";

import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AttachFileIcon from "@mui/icons-material/AttachFile";

import { updateShippingOrder } from "src/api/shippingOrder";

import font from "src/styles/font";
import { checkNotEmpty } from "src/common";
import { Black, Primary } from "src/styles/color";

import Dialog from "src/components/core/Dialog";
import Button from "src/components/core/Button";
import TextField from "src/components/core/TextField";
import UploadPdf from "src/components/upload/dropzone/UploadPdf";
import UploadFile from "src/components/upload/dropzone/UploadFile";
import {
  SpanLabel,
  SpanRemarkLabel,
  StyledDatePicker,
  StyledTextField,
  TextFieldDialog,
} from "src/modules/order/shipping-order/components";

const CloseButton = styled(IconButton)`
  &.MuiIconButton-root {
    color: ${Black};
  }
`;

const StyledDialog = muiStyled(Dialog)(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 16,
  },
  "& .MuiDialogContent-root": {
    maxHeight: "500px",
    border: 0,
    padding: 24,
    paddingTop: 0,
  },
  "& .MuiDialog-container": {
    "& .MuiPaper-root": {
      width: "100%",
      maxWidth: 640,
    },
  },
  "& .MuiDialogActions-root": {
    justifyContent: "center",
  },
  "& .MuiDialogActions-root>:not(:first-of-type)": {
    marginLeft: 24,
  },

  [theme.breakpoints.down("sm")]: {
    "& .MuiPaper-root": {
      margin: 24,
    },
    "& .MuiDialogContent-root": {
      padding: 16,
      paddingTop: 0,
    },
    "& .MuiDialogActions-root": {
      padding: 16,
    },
  },
}));

const QtDialog = ({
  onClose,
  onSuccess,
  orderDetail,
  mode = "add",
  setIsLoading,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const [shippingDetail, setShippingDetail] = useState({});

  useEffect(() => {
    if (!!orderDetail) {
      setShippingDetail({
        ...orderDetail,
        documents: {
          ...orderDetail.documents,
          seller: {
            ...orderDetail.documents?.seller,
            qt: {
              ...orderDetail.documents?.seller?.qt,
              sellerQtAdditionalFile:
                orderDetail?.documents?.seller?.qt?.sellerQtAdditionalFile,
              qtDate: orderDetail.documents?.seller?.qt?.qtDate,
              sellerQtFile: orderDetail?.documents?.seller?.qt?.sellerQtFile,
              qtNo: orderDetail?.documents?.seller?.qt?.qtNo,
            },
          },
        },
      });
    }
  }, [orderDetail]);

  const onSubmit = async () => {
    try {
      setIsLoading(true);
      const payload = {
        ...shippingDetail,
        order: shippingDetail?.order?._id,
      };

      const data = new FormData();
      for (const field in payload) {
        if (
          field === "order" ||
          field === "_id" ||
          field === "method" ||
          field === "note" ||
          field === "seller"
        ) {
          data.append(field, payload[field]);
        } else {
          data.append(field, JSON.stringify(payload[field]));
        }
      }

      let document = JSON.parse(data.get("documents"));
      delete document.seller.qt.sellerQtFile;
      delete document.seller.qt.sellerQtAdditionalFile;
      data.set("documents", JSON.stringify(document));

      let fulfillmentProcess = JSON.parse(data.get("fulfillmentProcess"));
      if (fulfillmentProcess?.[0]?.deliveryProofs?.length > 0) {
        let formatDeliveryProofs = fulfillmentProcess?.[0]?.deliveryProofs;

        formatDeliveryProofs?.map((item, index) => {
          let format = item;
          format.deliveryProof = item?.deliveryProof?._id;
          return format;
        });

        fulfillmentProcess[0] = {
          ...fulfillmentProcess[0],
          ...{ deliveryProofs: formatDeliveryProofs },
        };
      }
      if (fulfillmentProcess?.[0]?.shippingProofs?.length > 0) {
        let formatShippingProofs = fulfillmentProcess?.[0]?.shippingProofs;

        formatShippingProofs?.map((item, index) => {
          let format = item;
          format.shippingProof = item?.shippingProof?._id;
          return format;
        });

        fulfillmentProcess[0] = {
          ...fulfillmentProcess[0],
          ...{ shippingProofs: formatShippingProofs },
        };
      }
      data.set("fulfillmentProcess", JSON.stringify(fulfillmentProcess));

      if (
        typeof payload.documents.seller.qt.sellerQtFile === "object" &&
        payload.documents.seller.qt.sellerQtFile !== null
      ) {
        if (payload.documents.seller.qt.sellerQtFile.path) {
          data.append(
            "documents.seller.qt.sellerQtFile",
            renameFile(payload.documents.seller.qt.sellerQtFile),
          );
        } else {
          document.seller.qt.sellerQtFile =
            payload.documents.seller.qt.sellerQtFile._id;
          data.set("documents", JSON.stringify(document));
        }
      }

      if (
        typeof payload.documents.seller.qt.sellerQtAdditionalFile ===
          "object" &&
        payload.documents.seller.qt.sellerQtAdditionalFile !== null
      ) {
        if (payload.documents.seller.qt.sellerQtAdditionalFile.path) {
          data.append(
            "documents.seller.qt.sellerQtAdditionalFile",
            renameFile(payload.documents.seller.qt.sellerQtAdditionalFile),
          );
        } else {
          document.seller.qt.sellerQtAdditionalFile =
            payload.documents.seller.qt.sellerQtAdditionalFile._id;
          data.set("documents", JSON.stringify(document));
        }
      }
      data.append("isDocument", true);
      await updateShippingOrder(orderDetail._id, data);
      await onSuccess();
      enqueueSnackbar(`อัพเดทข้อมูลใบเสนอราคา สำเร็จ`, {
        variant: "success",
      });
    } catch (err) {
      enqueueSnackbar(err.response.data.message, {
        variant: "error",
      });
    }
  };

  const isFormValid = () => {
    return (
      (shippingDetail?.documents?.seller?.qt?.qtDate ||
        shippingDetail?.documents?.seller?.qt?.sellerQtFile?.createdAt) &&
      // shippingDetail?.documents?.seller?.qt?.qtDate &&
      shippingDetail?.documents?.seller?.qt?.qtNo &&
      shippingDetail?.documents?.seller?.qt?.sellerQtFile
      //   ||
      // (!shippingDetail?.documents?.seller?.inv?.invDate &&
      //   !shippingDetail?.documents?.seller?.inv?.invNo &&
      //   !shippingDetail?.documents?.seller?.inv?.invFile)
    );
  };

  const renameFile = (file) => {
    const randomPart = Math.floor(Math.random() * 100000);
    let newName = `QTSL${dayjs().format("DDMMYY")}-${randomPart}`;
    return new File([file], newName, {
      type: file.type,
      lastModified: file.lastModified,
    });
  };

  return (
    <StyledDialog
      onClose={onClose}
      aria-labelledby="customized-dialog-title"
      open={true}
    >
      <DialogTitle
        sx={{
          p: {
            sm: 3,
            xs: 2,
          },
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          fontSize: {
            sm: font.size.lg,
            xs: font.size.md,
          },
          fontWeight: 600,
        }}
      >
        {mode === "add" ? "เพิ่มเอกสาร" : "แก้ไขเอกสาร"}
        <CloseButton aria-label="close" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </CloseButton>
      </DialogTitle>

      <DialogContent>
        <Grid container xs={12}>
          <Grid
            item
            xs={12}
            sm={6}
            sx={{ pr: { xs: 0, sm: 2 }, mb: { xs: 2, sm: 0 } }}
          >
            <SpanLabel isMargin>
              วันที่ ใบเสนอราคา <span>*</span>
            </SpanLabel>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <StyledDatePicker
                inputFormat="DD-MM-YYYY"
                value={shippingDetail?.documents?.seller?.qt?.qtDate}
                onChange={(value) => {
                  setShippingDetail((prevState) => {
                    const updateShipping = prevState;
                    updateShipping.documents.seller.qt.qtDate =
                      value !== null ? value.$d : "";
                    return {
                      ...prevState,
                      ...updateShipping,
                    };
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    sx={{
                      width: "100%",
                      height: {
                        xs: 40,
                        sm: 48,
                      },
                    }}
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} sm={6} sx={{ pl: { xs: 0, sm: 2 } }}>
            <SpanLabel isMargin>
              {`ใบเสนอราคา No.`} <span>*</span>
            </SpanLabel>
            <TextFieldDialog
              variant="outlined"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={shippingDetail?.documents?.seller?.qt?.qtNo}
              onChange={({ target }) =>
                setShippingDetail((prevState) => {
                  const updateShipping = prevState;
                  updateShipping.documents.seller.qt.qtNo = target.value;
                  return {
                    ...prevState,
                    ...updateShipping,
                  };
                })
              }
            />
          </Grid>
        </Grid>

        <Grid container xs={12} mt={2}>
          <Grid item xs={12}>
            <SpanLabel isMargin>
              {`ใบเสนอราคา`} <span>*</span>
              <SpanRemarkLabel> {`(.pdf)`}</SpanRemarkLabel>
            </SpanLabel>
            <StyledTextField
              variant="outlined"
              fullWidth
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <>
                    {checkNotEmpty(
                      shippingDetail?.documents?.seller?.qt?.sellerQtFile,
                    ) ? (
                      <IconButton
                        onClick={() => {
                          const newShipping = shippingDetail;
                          newShipping.documents.seller.qt.sellerQtFile = "";
                          setShippingDetail({ ...newShipping });
                        }}
                      >
                        <DeleteOutlineIcon
                          sx={{
                            color: Primary,
                            cursor: "pointer",
                          }}
                        />
                      </IconButton>
                    ) : (
                      <UploadPdf
                        callBackFile={(fileCallBlack) => {
                          const newShipping = shippingDetail;
                          newShipping.documents.seller.qt.sellerQtFile =
                            fileCallBlack;
                          setShippingDetail({ ...newShipping });
                        }}
                        closeAlert={false}
                      >
                        <IconButton>
                          <AttachFileIcon
                            sx={{
                              color: Black,
                              cursor: "pointer",
                              fontSize: font.size.lg,
                              ":hover": {
                                color: "#e04132",
                              },
                            }}
                          />
                        </IconButton>
                      </UploadPdf>
                    )}
                  </>
                ),
              }}
              InputLabelProps={{ shrink: true }}
              value={
                typeof shippingDetail?.documents?.seller?.qt?.sellerQtFile !==
                "object"
                  ? shippingDetail?.documents?.seller?.qt?.sellerQtFile
                  : (shippingDetail?.documents?.seller?.qt?.sellerQtFile
                      ?.name ??
                    shippingDetail?.documents?.seller?.qt?.sellerQtFile
                      ?.originalFileName)
              }
            />
          </Grid>
        </Grid>

        <Grid container xs={12} mt={2}>
          <Grid item xs={12}>
            <SpanLabel isMargin>
              {`เอกสารเพิ่มเติม`}
              <SpanRemarkLabel> {`(.pdf, .jpg, .jpec, .png)`}</SpanRemarkLabel>
            </SpanLabel>
            <StyledTextField
              variant="outlined"
              fullWidth
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <>
                    {checkNotEmpty(
                      shippingDetail?.documents?.seller?.qt
                        ?.sellerQtAdditionalFile,
                    ) ? (
                      <IconButton
                        onClick={() => {
                          const newShipping = shippingDetail;
                          newShipping.documents.seller.qt.sellerQtAdditionalFile =
                            "";
                          setShippingDetail({ ...newShipping });
                        }}
                      >
                        <DeleteOutlineIcon
                          sx={{
                            color: Primary,
                            cursor: "pointer",
                          }}
                        />
                      </IconButton>
                    ) : (
                      <UploadFile
                        callBackFile={(fileCallBlack) => {
                          const newShipping = shippingDetail;
                          newShipping.documents.seller.qt.sellerQtAdditionalFile =
                            fileCallBlack;
                          setShippingDetail({ ...newShipping });
                        }}
                        closeAlert={false}
                        acceptFileType={[".pdf", ".jpg", ".jpeg", ".png"]}
                      >
                        <IconButton>
                          <AttachFileIcon
                            sx={{
                              color: Black,
                              cursor: "pointer",
                              fontSize: font.size.lg,
                              ":hover": {
                                color: "#e04132",
                              },
                            }}
                          />
                        </IconButton>
                      </UploadFile>
                    )}
                  </>
                ),
              }}
              InputLabelProps={{ shrink: true }}
              value={
                typeof shippingDetail?.documents?.seller?.qt
                  ?.sellerQtAdditionalFile !== "object"
                  ? shippingDetail?.documents?.seller?.qt
                      ?.sellerQtAdditionalFile
                  : (shippingDetail?.documents?.seller?.qt
                      ?.sellerQtAdditionalFile?.name ??
                    shippingDetail?.documents?.seller?.qt
                      ?.sellerQtAdditionalFile?.originalFileName)
              }
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          variant="outlined"
          color="secondary"
          size="large"
          onClick={() => onClose()}
          fullWidth
          sx={{ maxWidth: { xs: 135.5, sm: 180 }, height: { xs: 32, sm: 48 } }}
        >
          ยกเลิก
        </Button>

        <Button
          variant="contained"
          size="large"
          onClick={() => onSubmit()}
          fullWidth
          sx={{ maxWidth: { xs: 135.5, sm: 180 }, height: { xs: 32, sm: 48 } }}
          disabled={!isFormValid()}
        >
          ยืนยัน
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default QtDialog;
